/**
 * Shared defect calculation utilities
 * FT-180: Single source of truth for defect stats
 *
 * Used by:
 * - DefectsModule.js (module page stats)
 * - useDashboardCompliance.js (dashboard compliance card)
 * - DefectsSummaryCard.js (sidebar summary)
 */

/**
 * Check if a defect has a specific severity level
 * Checks across multiple fields: priority, severity, risk_level (all lowercase)
 *
 * @param {Object} defect - The defect record
 * @param {string} level - The severity level to check ('critical', 'high', 'medium', 'low')
 * @returns {boolean}
 */
export const hasSeverity = (defect, level) => {
  const priority = (defect.priority || '').toLowerCase();
  const severity = (defect.severity || '').toLowerCase();
  const riskLevel = (defect.risk_level || '').toLowerCase();
  return priority === level || severity === level || riskLevel === level;
};

/**
 * Check if a defect is considered "open" (not resolved or closed)
 * Uses case-insensitive comparison
 *
 * @param {Object} defect - The defect record
 * @returns {boolean}
 */
export const isOpenDefect = (defect) => {
  const status = (defect.status || '').toLowerCase();
  return status !== 'resolved' && status !== 'closed';
};

/**
 * Check if a defect is considered "resolved" (resolved or closed)
 * Uses case-insensitive comparison
 *
 * @param {Object} defect - The defect record
 * @returns {boolean}
 */
export const isResolvedDefect = (defect) => {
  const status = (defect.status || '').toLowerCase();
  return status === 'resolved' || status === 'closed';
};

/**
 * Calculate defect statistics from a list of defects
 * Single source of truth - used by both Dashboard and Defects Module
 *
 * @param {Array} defects - Array of defect records
 * @returns {Object} Stats object with counts and compliance percentage
 *
 * Stats shape:
 * {
 *   active: number,      // Open defects count
 *   critical: number,    // Critical severity (open only)
 *   high: number,        // High severity, not critical (open only)
 *   medium: number,      // Medium severity, not critical/high (open only)
 *   low: number,         // Low severity, not critical/high/medium (open only)
 *   resolved: number,    // Resolved/closed defects count
 *   total: number,       // Total defects count
 *   compliance: number   // Compliance percentage (0-100)
 * }
 */
export const calculateDefectStats = (defects = []) => {
  const defectList = Array.isArray(defects) ? defects : [];

  // Filter to actual defects (not repairs)
  const defectRecords = defectList.filter(d => d.record_type !== 'repair');
  const repairRecords = defectList.filter(d => d.record_type === 'repair');

  // Open defects (not resolved or closed)
  const openDefects = defectRecords.filter(isOpenDefect);

  // Count by severity (mutually exclusive - highest severity wins)
  const criticalCount = openDefects.filter(d => hasSeverity(d, 'critical')).length;
  const highCount = openDefects.filter(d =>
    hasSeverity(d, 'high') && !hasSeverity(d, 'critical')
  ).length;
  const mediumCount = openDefects.filter(d =>
    hasSeverity(d, 'medium') && !hasSeverity(d, 'critical') && !hasSeverity(d, 'high')
  ).length;
  const lowCount = openDefects.filter(d =>
    hasSeverity(d, 'low') && !hasSeverity(d, 'critical') && !hasSeverity(d, 'high') && !hasSeverity(d, 'medium')
  ).length;

  // Resolved count
  const resolvedCount = defectRecords.filter(isResolvedDefect).length;

  // FT-90: Penalty model compliance
  // Formula: MAX(0, 100 - (5 × medium + 20 × high + 100 × critical))
  const compliance = Math.max(0, 100 - (5 * mediumCount + 20 * highCount + 100 * criticalCount));

  return {
    active: openDefects.length,
    critical: criticalCount,
    high: highCount,
    medium: mediumCount,
    low: lowCount,
    resolved: resolvedCount,
    repairs: repairRecords.length,
    total: defectList.length,
    compliance
  };
};

/**
 * Get open defects from a list
 * Convenience function for filtering
 *
 * @param {Array} defects - Array of defect records
 * @returns {Array} Open defects only
 */
export const getOpenDefects = (defects = []) => {
  const defectList = Array.isArray(defects) ? defects : [];
  return defectList.filter(d => d.record_type !== 'repair').filter(isOpenDefect);
};

/**
 * Get critical defects from a list
 * Convenience function for filtering
 *
 * @param {Array} defects - Array of defect records
 * @returns {Array} Critical open defects only
 */
export const getCriticalDefects = (defects = []) => {
  return getOpenDefects(defects).filter(d => hasSeverity(d, 'critical'));
};
